// AttachmentsPanel — foto/súbory na ľubovoľnú entitu (sek. 59)
// Použitie:
//   <AttachmentsPanel entityType="project" entityId={zakazka.id} />
//   <AttachmentsPanel entityType="diary" entityId={zapis.id} />
//   <AttachmentsPanel entityType="loan_record" entityId={loan.id} />
import React, { useEffect, useState } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, SK } from '../theme.js'
import { useToast } from './Toast.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { Modal } from './Modal.jsx'

const PHASE_LABELS = {
  '': '—',
  pred: 'Pred prácou',
  po: 'Po práci',
  ine: 'Iné',
}

const PHASE_COLORS = {
  pred: '#3b82f6',
  po: '#22c55e',
  ine: '#94a3b8',
}

export default function AttachmentsPanel({ entityType, entityId, allowDelete = true, showPhase = true, onSetMain = null, mainUrl = '', onChanged = null }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [phase, setPhase] = useState('')
  const [caption, setCaption] = useState('')
  const toast = useToast()
  // prilohy_nahradit_v1 (19. 10. 2026, Mirko: „Jedno riešenie pre všetko“): Nahradiť s dôvodom, Odstrániť do koša,
  // staršie verzie ostávajú — server nič natvrdo nemaže.
  const [hist, setHist] = useState([])
  const [ukon, setUkon] = useState(null)      // { typ: 'nahradit' | 'odstranit', it, subor }
  const [dovod, setDovod] = useState('')
  const [dovodIny, setDovodIny] = useState('')
  const [pracuje, setPracuje] = useState(false)
  const suborRef = React.useRef(null)
  const ciel = React.useRef(null)

  function nacitaj() {
    if (!entityType || !entityId) return
    setLoading(true)
    fetchAuth(`/api/attachments?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false))
    fetchAuth(`/api/attachments/historia?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`)
      .then(r => r.ok ? r.json() : []).then(d => setHist(Array.isArray(d) ? d : [])).catch(() => setHist([]))
  }
  useEffect(nacitaj, [entityType, entityId])

  async function nahraj(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    let okCount = 0
    for (const f of files) {
      try {
        const fd = new FormData()
        fd.append('file', f)
        fd.append('entity_type', entityType)
        fd.append('entity_id', String(entityId))
        if (phase) fd.append('phase', phase)
        if (caption) fd.append('caption', caption)
        const r = await fetchAuth('/api/attachments', { method: 'POST', body: fd })
        if (r.ok) okCount++
        else { const d = await r.json().catch(() => ({})); toast.error('Súbor neuložený: ' + (d.error || r.status)) }
      } catch (err) { toast.error('Chyba: ' + err.message) }
    }
    if (okCount) toast.success(`Nahraných ${okCount} z ${files.length} súborov`)
    e.target.value = ''  // reset
    setUploading(false)
    setCaption('')
    nacitaj()
    if (okCount && onChanged) onChanged()
  }

  function zacniNahradit(it) { ciel.current = it; if (suborRef.current) { suborRef.current.value = ''; suborRef.current.click() } }
  function vybranySubor(e) {
    const f = (e.target.files || [])[0]
    if (!f || !ciel.current) return
    setDovod(''); setDovodIny(''); setUkon({ typ: 'nahradit', it: ciel.current, subor: f })
  }
  function zacniOdstranit(it) { setDovod(''); setDovodIny(''); setUkon({ typ: 'odstranit', it }) }
  async function potvrd() {
    const d = (dovod === 'iné' ? dovodIny : dovod).trim()
    if (!d) { toast.error('Vyber alebo napíš dôvod.'); return }
    setPracuje(true)
    try {
      let r
      if (ukon.typ === 'nahradit') {
        const fd = new FormData(); fd.append('file', ukon.subor); fd.append('dovod', d)
        r = await fetchAuth(`/api/attachments/${encodeURIComponent(ukon.it.id)}/nahradit`, { method: 'POST', body: fd })
      } else {
        r = await fetchAuth(`/api/attachments/${encodeURIComponent(ukon.it.id)}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dovod: d }) })
      }
      const res = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(res.error || ('Server vrátil ' + r.status)); return }
      toast.success(ukon.typ === 'nahradit' ? 'Nahradené — starý súbor je v Staršie verzie.' : 'Presunuté do koša.')
      setUkon(null); nacitaj(); if (onChanged) onChanged()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setPracuje(false) }
  }
  const DOVODY = ukon && ukon.typ === 'nahradit' ? ['zlý sken', 'chýba strana', 'podpísaná verzia', 'iný dokument', 'iné'] : ['nahratý omylom', 'patrí k inej zákazke', 'duplicita', 'iné']
  const btnMaly = (farba) => ({ background: 'transparent', border: `1px solid ${farba}`, borderRadius: RADIUS.sm, cursor: 'pointer', color: farba, fontSize: TYPO.sizes.xs, padding: '1px 7px', fontWeight: 700 })

  const isImg = (mime) => mime && mime.startsWith('image/')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
      {/* Upload riadok */}
      <div style={{
        padding: SPACE.md,
        background: 'rgba(59,130,246,0.04)',
        border: `1px dashed ${COLORS.nav}`,
        borderRadius: RADIUS.md,
        display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {showPhase && (
          <select value={phase} onChange={e => setPhase(e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm }}>
            <option value="">Bez označenia</option>
            <option value="pred">Pred prácou</option>
            <option value="po">Po práci</option>
            <option value="ine">Iné</option>
          </select>
        )}
        <input type="text" placeholder="Popis (voliteľne)…" value={caption}
          onChange={e => setCaption(e.target.value)}
          style={{ flex: 1, minWidth: 180, padding: '6px 10px', border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, background: COLORS.bg }} />
        {/* 📸 Odfotiť — na mobile otvorí priamo fotoaparát (capture); „predĺžený foťák" (Mirko 2026-06-09) */}
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', background: COLORS.add, color: '#fff', borderRadius: RADIUS.md,
          cursor: uploading ? 'wait' : 'pointer', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold,
          opacity: uploading ? 0.6 : 1,
        }}>
          {uploading ? '…' : 'Odfotiť'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={uploading} onChange={nahraj} />
        </label>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', background: COLORS.nav, color: '#fff', borderRadius: RADIUS.md,
          cursor: uploading ? 'wait' : 'pointer', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold,
          opacity: uploading ? 0.6 : 1,
        }}>
          {uploading ? 'Nahrávam…' : 'Súbor'}
          {/* zod_prilohy_v1: aj Word/Excel/ODT — ZoD a zmluvy chodia ako .docx; prilohy obrazku a PDF ostavaju */}
          <input type="file" multiple accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods" style={{ display: 'none' }} disabled={uploading} onChange={nahraj} />
        </label>
      </div>

      {/* Zoznam */}
      {loading ? (
        <div style={{ padding: SPACE.md, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
      ) : items.length === 0 ? (
        <div style={{
          padding: SPACE.lg, color: COLORS.muted, textAlign: 'center',
          background: COLORS.bgSoft, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm,
        }}>
          Žiadne súbory. Pridaj prvý cez tlačidlo vyššie.<br/>
          <span style={{ fontSize: TYPO.sizes.xs }}>Tip: označ fotky <b>Pred prácou / Po práci</b> — pomáha pri sporoch.</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: SPACE.sm }}>
          {items.map(it => (
            <div key={it.id} style={{
              border: `1px solid ${COLORS.line}`,
              borderTop: `4px solid ${PHASE_COLORS[it.phase] || COLORS.line}`,
              borderRadius: RADIUS.md, background: COLORS.bg, overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}>
              {isImg(it.mime) ? (
                <a href={it.url} target="_blank" rel="noreferrer" style={{ display: 'block', background: '#000' }}>
                  <img src={it.url} alt={it.fname} style={{
                    width: '100%', height: 140, objectFit: 'cover', display: 'block',
                  }} />
                </a>
              ) : (
                <a href={it.url} target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 140, background: COLORS.bgSoft, color: COLORS.muted,
                  fontSize: 48, textDecoration: 'none',
                }}>Otvoriť</a>
              )}
              <div style={{ padding: SPACE.sm, display: 'flex', flexDirection: 'column', gap: 2, fontSize: TYPO.sizes.xs }}>
                <div style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text, wordBreak: 'break-word' }}>{it.fname}</div>
                {it.caption && <div style={{ color: COLORS.muted, fontStyle: 'italic' }}>{it.caption}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span style={{ color: COLORS.muted }}>
                    {it.phase && <span style={{ marginRight: 4 }}>{PHASE_LABELS[it.phase]}</span>}
                    {it.size ? Math.round(it.size / 1024) + ' kB' : ''}
                  </span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {onSetMain && isImg(it.mime) && (
                      mainUrl && mainUrl === it.url
                        ? <span style={{ color: COLORS.add, fontWeight: 800, fontSize: TYPO.sizes.xs }}>● Miniatúra</span>
                        : <button onClick={() => onSetMain(it.url)} style={{ background: 'transparent', border: `1px solid ${COLORS.nav}`, borderRadius: RADIUS.sm, cursor: 'pointer', color: COLORS.nav, fontSize: TYPO.sizes.xs, padding: '1px 7px', fontWeight: 700 }} title="Použiť ako hlavnú miniatúru v zozname">Miniatúra</button>
                    )}
                    <button onClick={() => zacniNahradit(it)} style={btnMaly(COLORS.nav)} title="Nahrať novšiu verziu namiesto tejto — táto ostane v Staršie verzie">Nahradiť</button>
                    {allowDelete && (
                      <button onClick={() => zacniOdstranit(it)} style={btnMaly(COLORS.remove)} title="Len súbor nahratý omylom — ide do koša, dá sa obnoviť">Odstrániť</button>
                    )}
                  </span>
                </div>
                <div style={{ color: COLORS.muted, fontFamily: TYPO.mono, fontSize: 10 }}>
                  {it.uploaded_at?.slice(0, 16)} · {it.uploaded_by || '—'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <input ref={suborRef} type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods" style={{ display: 'none' }} onChange={vybranySubor} />

      {hist.length > 0 && (
        <details style={{ fontSize: TYPO.sizes.xs }}>
          <summary style={{ cursor: 'pointer', color: COLORS.muted }}>Staršie verzie a kôš ({hist.length})</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {hist.map(h => (
              <div key={h.id} style={{ padding: '6px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, background: COLORS.bgSoft }}>
                <a href={h.url} target="_blank" rel="noreferrer" style={{ fontWeight: TYPO.weights.semibold }}>{h.fname}</a>
                <span style={{ marginLeft: 6, color: h.stav === 'kos' ? COLORS.remove : COLORS.muted, fontWeight: 700 }}>{h.stav === 'kos' ? 'v koši' : 'nahradený'}</span>
                <div style={{ color: COLORS.muted }}>nahral {h.uploaded_by || '—'} {String(h.uploaded_at || '').slice(0, 16)} · {h.stav === 'kos' ? 'odstránil' : 'nahradil'} {h.zmena_kto || '—'} {String(h.zmena_at || '').slice(0, 16)} · dôvod: {h.dovod || '—'}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      <Modal open={!!ukon} onClose={() => !pracuje && setUkon(null)} maxWidth={480}
        title={ukon && ukon.typ === 'nahradit' ? 'Nahradiť dokument' : 'Odstrániť dokument'}
        subtitle={ukon ? ukon.it.fname : ''}>
        {ukon && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm, fontSize: TYPO.sizes.sm }}>
            {ukon.typ === 'nahradit'
              ? <div>Nový súbor: <b>{ukon.subor.name}</b>. Starý sa nezmaže — presunie sa do „Staršie verzie“.</div>
              : <div>Len pre súbor nahratý omylom. Ide do koša a dá sa obnoviť. Novšiu verziu nahraj cez <b>Nahradiť</b>.</div>}
            <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Dôvod (povinný)</label>
            <select value={dovod} onChange={e => setDovod(e.target.value)} style={{ padding: '6px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }}>
              <option value="">— vybrať —</option>
              {DOVODY.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {dovod === 'iné' && <input type="text" value={dovodIny} onChange={e => setDovodIny(e.target.value)} placeholder="Napíš dôvod" style={{ padding: '6px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }} />}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: SPACE.sm }}>
              <button onClick={() => setUkon(null)} disabled={pracuje} style={btnMaly(COLORS.muted)}>Zrušiť</button>
              <button onClick={potvrd} disabled={pracuje} style={Object.assign(btnMaly(ukon.typ === 'nahradit' ? COLORS.nav : COLORS.remove), { padding: '4px 12px' })}>{pracuje ? '…' : (ukon.typ === 'nahradit' ? 'Nahradiť' : 'Odstrániť do koša')}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
